import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const LOGO_MIN_MS = 900;

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useTranslation(['auth', 'common']);

  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [error, setError]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const mountTime = useRef(Date.now());

  useEffect(() => {
    const elapsed = Date.now() - mountTime.current;
    const delay   = Math.max(0, LOGO_MIN_MS - elapsed);
    const t = setTimeout(() => setFormVisible(true), delay);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (user) navigate('/maintenance', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password || submitting) return;
    setSubmitting(true);
    setError('');
    const result = await login(email.trim(), password);
    if (result.errorKey) {
      setError(t(result.errorKey));
      setSubmitting(false);
    } else if (result.error) {
      setError(result.error);
      setSubmitting(false);
    } else {
      // Full-page reload instead of client-side navigate — avoids a rendering
      // crash that occurs on first sign-in when transitioning from /login to /.
      // Equivalent to the user manually refreshing after login (which always works).
      window.location.replace('/maintenance');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-6">

      {/* Hot runner — molten brand hairline */}
      <span aria-hidden className="fixed inset-x-0 top-0 h-[2px] hot-runner z-10" />

      {/* Logo — large + centred while loading, smaller at top once form is ready */}
      <div
        className={cn(
          'flex flex-col items-center transition-all ease-out',
          formVisible
            ? 'mt-[10vh] duration-500'
            : 'mt-[calc(50vh-9rem)] duration-0'
        )}
      >
        <img
          src="/logo.png"
          alt="MoldOp"
          className={cn(
            'object-contain select-none transition-all ease-out mix-blend-multiply dark:mix-blend-normal',
            'animate-in fade-in zoom-in-95 duration-700',
            formVisible ? 'w-44 duration-500' : 'w-72 duration-0',
          )}
        />

        {/* Sliding bar — visible only while form hasn't appeared yet */}
        <div
          className={cn(
            'mt-8 w-36 h-[2px] rounded-full bg-border overflow-hidden transition-opacity duration-500',
            formVisible ? 'opacity-0' : 'opacity-100'
          )}
        >
          <div
            className="h-full w-1/3 rounded-full hot-runner"
            style={{ animation: 'slide-bar 1.4s cubic-bezier(0.4,0,0.2,1) infinite' }}
          />
        </div>
      </div>

      {/* Form — fades + slides in once formVisible */}
      <div
        className={cn(
          'w-full max-w-sm mt-8 transition-all duration-500 ease-out',
          formVisible
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-3 pointer-events-none'
        )}
      >
        <div className="bg-card border border-border/70 rounded-2xl shadow-md overflow-hidden">
        <span aria-hidden className="block h-[2px] hot-runner" />
        <div className="p-8">
        <p className="text-sm text-muted-foreground text-center mb-6">
          {t('auth:sign_in_subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth:email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus={formVisible}
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              placeholder={t('auth:email_placeholder')}
              className="h-11"
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth:password')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder={t('auth:password_placeholder')}
              className="h-11"
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button
            type="submit"
            className="w-full h-11"
            disabled={!email.trim() || !password || submitting}
          >
            {submitting ? t('auth:signing_in') : t('auth:sign_in')}
          </Button>
        </form>
        </div>
        </div>
      </div>

    </div>
  );
}
